import type { FilterRuleModel, ProductCatalogModel } from '@/shared/generated'
import type { z } from 'zod'

import { useMemo, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { InfoIcon, RotateCcwIcon } from 'lucide-react'
import { useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useDebounceValue } from 'usehooks-ts'

import {
	useAnchorsProductCatalogFiltersQuery,
	useAnchorsProductCatalogQuery,
	useUpsertAnchorsMutation,
} from '@/entities/anchor'

import AsyncCombobox from '@/shared/components/AsyncCombobox'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import {
	FormProvider,
	RHFSelect,
	RHFTextField,
} from '@/shared/components/HookForm'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Blanket } from '@/shared/components/ui/blanket'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent } from '@/shared/components/ui/card'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import { Loader } from '@/shared/components/ui/loader'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { ampli } from '@/shared/lib/ampli'
import { queryClient } from '@/shared/lib/query-client'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'
import { processingMessage } from '@/shared/tools/message'

import {
	generateSourceFamilyId,
	getAnchorTypesOptionsList,
	hasSingleSalesEntity,
} from '../lib/helpers'
import { useAnchorsProductCatalog } from '../lib/useModalData'
import { formSchema } from '../model/contract'
import AnchorsMasterTable from './AnchorProductsTable'

type AssignToAnchorManagementModalProps = {
	isOpen: boolean
	close: () => void
	products: Record<string, string[]>
}

export function AssignToAnchorManagementModal({
	isOpen,
	close,
	products,
}: AssignToAnchorManagementModalProps) {
	const { t } = useTranslation()

	const { tableProductsCatalog, isLoading } = useAnchorsProductCatalog(products)
	const upsertAnchorsMutation = useUpsertAnchorsMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['anchor-assortment'],
			})
			close()
		},
	})
	const methods = useForm({
		resolver: zodResolver(formSchema),
		values: {
			anchors: tableProductsCatalog || [],
		} as z.infer<typeof formSchema>,
	})
	const anchorProduct = methods.watch('anchor_product')
	const _handleSubmit = (data: z.infer<typeof formSchema>) => {
		ampli.tableAssignToAnchorsSave()
		const anchorType = data.anchor_type
		const payload = {
			anchors: data.anchors.map(({ mul, add, sku, sales_entity }) => {
				const isAnchorProduct = sku === anchorProduct!.sku
				const indexes = isAnchorProduct
					? {}
					: {
							...(!!mul &&
								mul !== 0 && {
									[`${anchorType}_anchor_mul`]: mul,
								}),
							...(!!add && add !== 0 && { [`${anchorType}_anchor_add`]: add }),
							[`${anchorType}_anchor_sku`]: anchorProduct!.sku,
						}

				return {
					sku,
					sales_entity_id: sales_entity.id,
					optimization_group_id: '',
					[`${anchorType}_source_family_id`]: data.source_family_id,
					...indexes,
				}
			}),
		}
		toast.promise(upsertAnchorsMutation.mutateAsync(payload), {
			success: t('toast.entity.updated', {
				entity: t('entity.anchor_management'),
			}),
			error: t('fatal_error_title'),
			loading: processingMessage(t),
			id: 'anchoring_list_insert',
		})
	}
	const handleSubmit = methods.handleSubmit(_handleSubmit)
	const canProductsBeAssigned = useMemo(
		() => hasSingleSalesEntity(tableProductsCatalog),
		[tableProductsCatalog]
	)

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent
				className='flex h-[calc(100vh-2rem)] max-w-5xl flex-col'
				data-testid='products-assign-to-anchor-management-modal'
			>
				<ModalHeader className='space-y-2'>
					<ModalTitle>
						{t('anchoring_management.assign_to_modal.title', {
							defaultValue: 'Assign to anchor management',
						})}
					</ModalTitle>
				</ModalHeader>
				<ErrorBoundary>
					<ModalBody className='-mx-4 flex flex-col overflow-hidden px-0'>
						<FormProvider
							className='flex w-full flex-1 flex-col gap-5 overflow-hidden px-4'
							methods={methods}
							onSubmit={handleSubmit}
						>
							<Blanket
								className='absolute flex items-center justify-center rounded-lg bg-white/50 backdrop-blur-lg dark:bg-black/50'
								isTinted={isLoading}
							>
								<Loader />
							</Blanket>
							<div className='flex w-full items-start gap-3'>
								{/* Anchor type selection */}
								<div className='flex-1'>
									<RHFSelect
										data-testid='modal-assign-to-anchor-management-select-anchor-type'
										name='anchor_type'
										placeholder={t(
											'anchoring_management.assign_to_modal.anchor_type',
											{ defaultValue: 'Select anchor type' }
										)}
										className='w-full'
										label={t('anchoring_type')}
										options={getAnchorTypesOptionsList(t)}
									/>
								</div>
								{/* Family ID selection */}
								<div className='flex-1'>
									<RHFTextField
										data-testid='modal-assign-to-anchor-management-input-source-family-id'
										name='source_family_id'
										placeholder={t(
											'anchoring_management.assign_to_modal.source_family_id.placeholder',
											{ defaultValue: 'Select family ID' }
										)}
										className='w-full'
										label={t(
											'anchoring_management.assign_to_modal.source_family_id.label'
										)}
										iconAfter={
											<Button
												onClick={() =>
													methods.setValue(
														'source_family_id',
														generateSourceFamilyId()
													)
												}
												size='icon-sm'
												variant='ghost'
											>
												<RotateCcwIcon />
											</Button>
										}
									/>
								</div>
								{/* Main product selection */}
								<MainProductSelection products={products} />
							</div>
							{!canProductsBeAssigned && (
								<Alert variant='neutral'>
									<InfoIcon />
									<AlertDescription>
										{t(
											'anchoring_management.assign_to_modal.sales_entity_id.error'
										)}
									</AlertDescription>
								</Alert>
							)}
							{!!anchorProduct && (
								<AnchorProductCard anchorProduct={anchorProduct} />
							)}
							<div className='flex min-h-0 flex-1 flex-col'>
								<div className='flex min-h-0 flex-1 overflow-hidden rounded-lg border'>
									<AnchorsMasterTable
										tableData={methods?.watch('anchors') || []}
										anchorProduct={anchorProduct}
									/>
								</div>
							</div>
						</FormProvider>
					</ModalBody>
				</ErrorBoundary>
				<ModalFooter>
					<ModalClose asChild>
						<Button variant='secondary'>{t('app.cancel')}</Button>
					</ModalClose>
					<Button
						variant='warning'
						data-testid='products-assign-to-anchor-management-save'
						onClick={handleSubmit}
					>
						{t('general_save')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	)
}
function AnchorProductCard({
	anchorProduct,
}: {
	anchorProduct: ProductCatalogModel
}) {
	const { t } = useTranslation()

	return (
		<Card className='shadow-none'>
			<CardContent>
				<DescriptionList className='flex items-start gap-10'>
					<DescriptionGroup>
						<DescriptionTerm>
							{t('anchoring_management.anchor_sku.title', {
								defaultValue: 'Main SKU',
							})}
						</DescriptionTerm>
						<DescriptionDetail>
							<div className='inline-flex items-center space-x-1'>
								<div>{anchorProduct.sku}</div>
							</div>
						</DescriptionDetail>
					</DescriptionGroup>
					<DescriptionGroup>
						<DescriptionTerm>{t('title')}</DescriptionTerm>
						<DescriptionDetail>
							<div className='inline-flex items-center space-x-1'>
								<div>{anchorProduct.title}</div>
							</div>
						</DescriptionDetail>
					</DescriptionGroup>
					<DescriptionGroup>
						<DescriptionTerm>
							{t('app.cost', { defaultValue: 'Cost' })}
						</DescriptionTerm>
						<DescriptionDetail>
							<div className='inline-flex items-center space-x-1'>
								<div>
									{formatNumber(anchorProduct.cost, NumberFormats.Fixed)}
								</div>
							</div>
						</DescriptionDetail>
					</DescriptionGroup>
					<DescriptionGroup>
						<DescriptionTerm>{t('app.price')}</DescriptionTerm>
						<DescriptionDetail>
							<div className='inline-flex items-center space-x-1'>
								<div>
									{formatNumber(anchorProduct.last_price, NumberFormats.Fixed)}
								</div>
							</div>
						</DescriptionDetail>
					</DescriptionGroup>
				</DescriptionList>
			</CardContent>
		</Card>
	)
}

function MainProductSelection({
	products,
}: {
	products: Record<string, string[]>
}) {
	const { t } = useTranslation()

	const { control } = useFormContext()
	const [searchText, setSearchText] = useState('')
	const [debouncedSearchText] = useDebounceValue(searchText, 500)
	const { data: anchorsProductCatalogFilters } =
		useAnchorsProductCatalogFiltersQuery(products, {})
	const requestBody = {
		filters: [
			[
				...(anchorsProductCatalogFilters?.map((item) =>
					item.find((item) => item.name === 'sales_entity_id')
				) || []),
			] as FilterRuleModel[],
		],
		limit: 50,
		search: debouncedSearchText || undefined,
	}
	const { data: anchorsProductCatalog, isFetching } =
		useAnchorsProductCatalogQuery(requestBody, {
			enabled: !!anchorsProductCatalogFilters,
		})
	const anchorsProductCatalogOptions = useMemo(
		() =>
			anchorsProductCatalog?.data?.map((item) => ({
				label: `${item.sku} - ${item.title}`,
				value: item.sku,
			})),
		[anchorsProductCatalog]
	)

	return (
		<div className='flex-1 space-y-2'>
			<FormField
				name='anchor_product'
				control={control}
				render={({ field: { onChange, value }, fieldState: { error } }) => (
					<FormItem>
						<FormLabel>
							{t('anchoring_management.modal.anchor_product_search.label', {
								defaultValue: 'Select main SKU',
							})}
						</FormLabel>
						<FormControl>
							<AsyncCombobox
								data-testid='modal-assign-to-anchor-management-anchor-product-search'
								error={error}
								className='text-foreground'
								value={value?.sku || ''}
								onSearchChange={setSearchText}
								setValue={(sku) =>
									onChange(
										anchorsProductCatalog?.data?.find(
											(item) => item.sku === sku
										)
									)
								}
								onClear={() => {
									onChange(null)
									setSearchText('')
								}}
								isLoading={isFetching}
								placeholder={t('products_search')}
								options={anchorsProductCatalogOptions || []}
							/>
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
		</div>
	)
}
